/**
 * Custom Recharge Widget
 * A production-ready subscription widget that integrates with Recharge selling plans
 * 
 * @version 1.0.0
 * @author Mood Co
 */

class RechargeCustomWidget {
  constructor() {
    this.widget = null;
    this.productId = null;
    this.selectedPlan = null;
    this.productForm = null;
    this.autoAddItems = []; // Parsed from data attribute on widget element
    
    // Configuration mapping selling plan IDs to our custom options
    this.config = {
      sellingPlans: {
        loyalty: {
          id: '710907855228',
          name: 'Loyalty Plan',
          discount: 25,
          price: 35.25,
          subtitle: 'Our most popular plan',
          commitment: '3-month commitment',
          features: [
            'Delivered monthly',
            'Delay, skip or amend frequency to suit your needs',
            '3-month commitment'
          ]
        },
        onetime: {
          id: null,
          name: 'One time purchase',
          discount: 0,
          price: 44.00,
          subtitle: 'Single bottle',
          commitment: null,
          features: [
            'One month\'s supply'
          ]
        }
      }
    };
  }

  /**
   * Initialize the widget
   */
  async init() {
    try {
      this.widget = document.querySelector('.custom-recharge-widget');
      if (!this.widget) return;

      this.productId = this.getProductId();
      this.productForm = this.getProductForm();
      if (!this.productId || !this.productForm) return;

      // Parse auto-add items from data attribute
      // Format: "variantId:trigger:discountCode,variantId:trigger:discountCode".
      this.autoAddItems = this.parseAutoAddItems(this.widget.dataset.autoAddItems);

      this.checkPromoCode();
      this.updateDiscountGatedFreeBadge();
      this.updateDiscountGatedOfferMedia();
      this.updateDiscountGatedDynamicCheckout();

      const collapseHost = this.widget.closest('[data-collapse-recharge-plans]');
      const collapsePanel = collapseHost ? this.widget.querySelector('.custom-recharge-plans-panel') : null;
      const collapseCta = collapseHost ? this.widget.querySelector('.custom-recharge-reveal-cta') : null;

      if (collapseHost && collapseCta && collapsePanel) {
        collapseCta.addEventListener('click', () => {
          collapseHost.classList.add('is-recharge-plans-visible');
          collapsePanel.hidden = false;
          collapseCta.setAttribute('aria-expanded', 'true');
          this.setInitialSelection();
        });
      }

      try {
        await this.waitForRechargeSDK();
      } catch {
        if (collapseHost && collapsePanel) {
          collapseHost.classList.add('is-recharge-plans-visible');
          collapsePanel.hidden = false;
          if (collapseCta) collapseCta.setAttribute('aria-expanded', 'true');
          this.setInitialSelection();
        }
      }

      this.bindEvents();
      this.interceptFormSubmission();

      if (!collapseHost) {
        this.setInitialSelection();
      }
    } catch {
      RechargeCustomWidget.revealCollapsedPlansFallback();
    }
  }

  /**
   * If init fails after partial setup, ensure progressive-disclosure pages still show plans + buy buttons.
   */
  static revealCollapsedPlansFallback() {
    const widget = document.querySelector('.custom-recharge-widget');
    if (!widget) return;
    const host = widget.closest('[data-collapse-recharge-plans]');
    if (!host) return;
    const panel = widget.querySelector('.custom-recharge-plans-panel');
    const cta = widget.querySelector('.custom-recharge-reveal-cta');
    host.classList.add('is-recharge-plans-visible');
    if (panel) panel.hidden = false;
    if (cta) cta.setAttribute('aria-expanded', 'true');

    const inst = window.rechargeCustomWidget;
    if (inst && inst.widget && inst.productForm && typeof inst.setInitialSelection === 'function') {
      try {
        inst.setInitialSelection();
      } catch (e) {
        /* ignore */
      }
    }
  }


  /**
   * Check URL for promo code and store in session storage
   */
  async checkPromoCode() {
    
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      if (cart.discount_codes) {
        const hasStarterCode = cart.discount_codes.some(
          discount => discount.code === 'GOODGUT' || discount.code === 'BEMINE' || discount.code === 'HAPPYGUT'
        );
        
        if (hasStarterCode) {
          this.updateLoyaltyDiscount();
        }
      }
    } catch (error) {
      // Silently fail if cart fetch fails
      console.error('Failed to check cart discounts:', error);
    }
  }

  async getCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error('Failed to fetch cart');
    }
    return response.json();
  }

  hasDiscountCode(cart, discountCode) {
    if (!discountCode || !cart || !Array.isArray(cart.discount_codes)) {
      return false;
    }

    const requiredCode = discountCode.toLowerCase();
    return cart.discount_codes.some((discount) => {
      return discount && typeof discount.code === 'string' && discount.code.toLowerCase() === requiredCode;
    });
  }

  cartContainsVariant(cart, variantId) {
    if (!variantId || !cart || !Array.isArray(cart.items)) {
      return false;
    }

    return cart.items.some((item) => String(item.id) === String(variantId));
  }

  async updateDiscountGatedFreeBadge() {
    const freeBadge = this.widget ? this.widget.querySelector('[data-discount-gated-free-badge]') : null;
    if (!freeBadge) return;

    const isSubscriptionSelected = this.isSubscriptionPlanSelected();
    if (isSubscriptionSelected && this.hasSubscriptionAutoAddItems()) {
      freeBadge.hidden = false;
      return;
    }

    const discountCodeItems = this.getDiscountCodeAutoAddItems();
    if (!isSubscriptionSelected || discountCodeItems.length === 0) {
      freeBadge.hidden = true;
      return;
    }

    try {
      const cart = await this.getCart();
      freeBadge.hidden = !discountCodeItems.some((item) => this.hasDiscountCode(cart, item.discountCode));
    } catch (error) {
      console.error('Failed to check free product badge discount:', error);
      freeBadge.hidden = true;
    }
  }

  getDiscountCodeAutoAddItems() {
    return this.autoAddItems.filter((item) => item.trigger === 'discount_code' && item.discountCode);
  }

  hasSubscriptionAutoAddItems() {
    return this.autoAddItems.some((item) => item.trigger === 'subscription');
  }

  isSubscriptionPlanSelected() {
    if (this.selectedPlan) {
      return this.selectedPlan.type === 'loyalty';
    }

    const checkedPlan = this.widget ? this.widget.querySelector('.recharge-radio:checked') : null;
    return checkedPlan ? checkedPlan.value === 'loyalty' : false;
  }

  getOfferCarouselImageConfig() {
    if (!this.widget || !this.widget.dataset.offerCarouselImageSrc) {
      return null;
    }

    return {
      src: this.widget.dataset.offerCarouselImageSrc,
      srcset: this.widget.dataset.offerCarouselImageSrcset || '',
      alt: this.widget.dataset.offerCarouselImageAlt || '',
      width: this.widget.dataset.offerCarouselImageWidth || '',
      height: this.widget.dataset.offerCarouselImageHeight || ''
    };
  }

  async updateDiscountGatedOfferMedia() {
    const imageConfig = this.getOfferCarouselImageConfig();
    if (!imageConfig) return;

    const isOfferActive = await this.isDiscountCodeOfferActive('offer carousel image');
    this.setOfferCarouselImageActive(isOfferActive, imageConfig);
  }

  async updateDiscountGatedDynamicCheckout() {
    const dynamicCheckout = this.productForm
      ? this.productForm.querySelector('.dynamic-checkout-buttons')
      : null;
    if (!dynamicCheckout) return;

    const isOfferActive = await this.isDiscountCodeOfferActive('dynamic checkout');
    dynamicCheckout.hidden = isOfferActive;
  }

  async isDiscountCodeOfferActive(label) {
    const discountCodeItems = this.getDiscountCodeAutoAddItems();
    if (discountCodeItems.length === 0) return false;

    try {
      const cart = await this.getCart();
      return discountCodeItems.some((item) => this.hasDiscountCode(cart, item.discountCode));
    } catch (error) {
      console.error(`Failed to check ${label} discount:`, error);
      return false;
    }
  }

  setOfferCarouselImageActive(isActive, imageConfig) {
    const imageSelectors = [
      '.product-media-carousel__slide:first-child .product-media__image',
      '.product-media-carousel-mobile__slide:first-child .product-media__image',
      '.product-thumbnail-carousel__thumb:first-child img',
      '.product-thumbnail-carousel-mobile__thumb:first-child img'
    ];

    document.querySelectorAll(imageSelectors.join(',')).forEach((image) => {
      this.setOfferImageState(image, isActive, imageConfig);
    });
  }

  setOfferImageState(image, isActive, imageConfig) {
    if (!image || !imageConfig) return;

    if (!image.dataset.originalSrc) {
      image.dataset.originalSrc = image.getAttribute('src') || '';
      image.dataset.originalSrcset = image.getAttribute('srcset') || '';
      image.dataset.originalAlt = image.getAttribute('alt') || '';
      image.dataset.originalWidth = image.getAttribute('width') || '';
      image.dataset.originalHeight = image.getAttribute('height') || '';
    }

    if (isActive) {
      image.src = imageConfig.src;
      if (imageConfig.srcset) image.srcset = imageConfig.srcset;
      image.alt = imageConfig.alt;
      if (imageConfig.width) image.setAttribute('width', imageConfig.width);
      if (imageConfig.height) image.setAttribute('height', imageConfig.height);
      return;
    }

    image.src = image.dataset.originalSrc;
    if (image.dataset.originalSrcset) {
      image.srcset = image.dataset.originalSrcset;
    } else {
      image.removeAttribute('srcset');
    }
    image.alt = image.dataset.originalAlt;
    if (image.dataset.originalWidth) image.setAttribute('width', image.dataset.originalWidth);
    if (image.dataset.originalHeight) image.setAttribute('height', image.dataset.originalHeight);
  }

  /**
   * Update loyalty discount display based on session storage
   */
  updateLoyaltyDiscount() {
    const loyaltyOption = this.widget.querySelector('[data-plan="loyalty"]');
   
    if (loyaltyOption) {

      const discountElement = loyaltyOption.querySelector('.option-discount');
      const discountPrice = loyaltyOption.querySelector('.option-price');
      const discountSubTitle = loyaltyOption.querySelector('.option-subtitle');
      const discountInput = loyaltyOption.querySelector('#loyalty-plan'); 
      loyaltyOption.classList.add('promo');

      if (discountElement && discountPrice && discountSubTitle) {
        discountSubTitle.textContent = 'Our most popular plan. Half price for your first month.'
        discountElement.textContent = 'Save 50%';
        discountPrice.textContent = "£17.60";
        discountInput.dataset.price = 17.60;
        this.updatePriceDisplay(17.60);
      }
    }
  }


  /**
   * Wait for Recharge SDK to be available
   */
  waitForRechargeSDK() {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds
      
      const checkSDK = () => {
        attempts++;
        
        if (window.Recharge || window.RechargeSDK || window.rc) {
          resolve();
        } else if (attempts >= maxAttempts) {
          reject(new Error('Recharge SDK not found'));
        } else {
          setTimeout(checkSDK, 100);
        }
      };
      
      checkSDK();
    });
  }

  /**
   * Get product ID from the page
   */
  getProductId() {
    // Try multiple methods to get product ID
    const productIdElement = document.querySelector('#custom-recharge-product-id, #subscription-product-id');
    if (productIdElement) {
      return productIdElement.value;
    }

    // Try from form
    const form = document.querySelector('.product-form form, form[action*="/cart/add"]');
    if (form) {
      const idInput = form.querySelector('input[name="id"]');
      if (idInput) {
        return idInput.value;
      }
    }

    // Try from URL or meta tags
    const productMeta = document.querySelector('meta[property="product:id"]');
    if (productMeta) {
      return productMeta.content;
    }

    return null;
  }

  /**
   * Get the product form
   */
  getProductForm() {
    return document.querySelector('.product-form form, form[action*="/cart/add"]');
  }

  /**
   * Parse auto-add items from data attribute
   * Format: "variantId:trigger:discountCode,variantId:trigger:discountCode"
   * @returns {Array<{variantId: string, trigger: string, discountCode: string}>}
   */
  parseAutoAddItems(raw) {
    if (!raw) return [];
    return raw.split(',').map(entry => {
      const [variantId, trigger, discountCode] = entry.split(':');
      return {
        variantId: variantId.trim(),
        trigger: (trigger || 'always').trim(),
        discountCode: (discountCode || '').trim()
      };
    }).filter(item => item.variantId);
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    const radioButtons = this.widget.querySelectorAll('.recharge-radio');
    
    radioButtons.forEach(radio => {
      radio.addEventListener('change', (e) => {
        if (e.target.checked) {
          this.handlePlanSelection(e.target);
        }
      });
    });
  }

  /**
   * Intercept form submission to add loyalty bonus product
   */
  interceptFormSubmission() {
    if (!this.productForm) return;

    this.productForm.addEventListener('submit', async (e) => {
      // Check if we need to intercept (loyalty plan or auto-add items)
      const isLoyaltyPlan = this.selectedPlan && this.selectedPlan.type === 'loyalty';
      const hasAlwaysItems = this.autoAddItems.some(item => item.trigger === 'always');
      const hasDiscountCodeItems = this.autoAddItems.some(item => item.trigger === 'discount_code');
      const needsIntercept = isLoyaltyPlan || hasAlwaysItems || hasDiscountCodeItems;
      
      if (needsIntercept) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Get form data
        const formData = new FormData(this.productForm);
        const items = [{
          id: formData.get('id'),
          quantity: parseInt(formData.get('quantity')) || 1
        }];
        
        // Add selling plan if present
        if (formData.get('selling_plan')) {
          items[0].selling_plan = formData.get('selling_plan');
        }
        
        // Add properties if present
        const properties = {};
        for (let [key, value] of formData.entries()) {
          if (key.startsWith('properties[')) {
            const propName = key.match(/properties\[(.*?)\]/)[1];
            properties[propName] = value;
          }
        }
        if (Object.keys(properties).length > 0) {
          items[0].properties = properties;
        }
        
        let cart = null;
        if (hasDiscountCodeItems) {
          try {
            cart = await this.getCart();
          } catch (error) {
            console.error('Failed to check auto-add discount:', error);
          }
        }

        // Auto-add configured products based on trigger rules
        this.autoAddItems.forEach(item => {
          if (item.trigger === 'always') {
            items.push({ id: item.variantId, quantity: 1 });
          } else if (item.trigger === 'subscription' && isLoyaltyPlan) {
            items.push({ id: item.variantId, quantity: 1 });
          } else if (
            item.trigger === 'discount_code'
            && isLoyaltyPlan
            && this.hasDiscountCode(cart, item.discountCode)
            && !this.cartContainsVariant(cart, item.variantId)
          ) {
            items.push({ id: item.variantId, quantity: 1 });
          }
        });
        
        // Check if gift wrap is selected and add it (integrating with gift-wrap.js)
        const giftWrapCheckbox = document.querySelector('[data-gift-wrap-checkbox]');
        if (giftWrapCheckbox && giftWrapCheckbox.checked) {
          items.push({
            id: '56003693773180',
            quantity: 1
          });
        }
        
        try {
          // Add all products to cart
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ items })
          });
          
          if (!response.ok) {
            throw new Error('Failed to add products to cart');
          }
          
          const result = await response.json();
          
          // Refresh cart UI if available
          if (window.theme && window.theme.cart && typeof window.theme.cart.refresh === 'function') {
            window.theme.cart.refresh();
          }
          
          // Dispatch event for cart updates
          document.dispatchEvent(new CustomEvent('cart:updated'));
          
          // Trigger any cart drawer/notification
          const cartDrawer = document.querySelector('cart-drawer, cart-notification');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
          
        } catch (error) {
          console.error('Error adding products to cart:', error);
          // Don't fall back to default submission to avoid duplicates
        }
        
        return false;
      }
    }, true); // Use capture phase to ensure we catch it first
  }

  /**
   * Set initial selection (default to loyalty plan)
   */
  setInitialSelection() {
    const defaultRadio = this.widget.querySelector('input[checked], .recharge-radio[data-price]');
    if (defaultRadio) {
      this.handlePlanSelection(defaultRadio);
    }
  }

  /**
   * Handle plan selection
   */
  handlePlanSelection(radioElement) {
    const plan = radioElement.value;
    const price = parseFloat(radioElement.dataset.price);
    const discount = parseInt(radioElement.dataset.discount) || 0;
    const rechargeOption = radioElement.closest('.recharge-option');
    const sellingPlanId = rechargeOption ? rechargeOption.dataset.sellingPlanId : null;

    this.selectedPlan = {
      type: plan,
      price: price,
      discount: discount,
      sellingPlanId: sellingPlanId || null
    };

    // Update UI
    this.updateSelectedOptionUI(radioElement);
    this.updatePriceDisplay(price);
    this.updateMsbAddToCartButtonLabel(price);

    // Update Shopify form
    this.updateProductForm();
    this.updateDiscountGatedFreeBadge();
    
    // Fire custom event for other integrations
    this.fireSelectionEvent();
  }

  /**
   * Update selected option UI
   */
  updateSelectedOptionUI(selectedRadio) {
    // Remove selected class from all options
    this.widget.querySelectorAll('.recharge-option').forEach(option => {
      option.classList.remove('selected');
    });
    
    // Add selected class to current option
    const selectedOption = selectedRadio.closest('.recharge-option');
    if (selectedOption) {
      selectedOption.classList.add('selected');
    }
  }

  /**
   * MSB subscribe template only: primary button label matches selected plan price
   */
  updateMsbAddToCartButtonLabel(price) {
    const scope = document.querySelector('.product--multi-strain-biotic-subsc');
    if (!scope || price == null || Number.isNaN(Number(price))) return;
    const formatted = `Start now - £${Number(price).toFixed(2)}`;
    scope.querySelectorAll('product-form [data-button-text]').forEach((el) => {
      el.textContent = formatted;
    });
  }

  /**
   * Update price display throughout the page
   */
  updatePriceDisplay(price) {
    const priceElements = document.querySelectorAll(
      '.product-price__current-price, .price__current, .price'
    );
    
    priceElements.forEach(element => {
      if (element && !element.closest('.custom-recharge-widget')) {
        element.textContent = `£${price.toFixed(2)}`;
      }
    });

    // Show/hide compare at price for subscriptions
    const compareElements = document.querySelectorAll(
      '.product-price__compare-price, .price__compare'
    );
    
    compareElements.forEach(element => {
        const originalPrice = 44.00;
        element.textContent = `£${originalPrice.toFixed(2)}`;
        element.style.display = 'inline';
    });
  }

  /**
   * Update the product form with selling plan information
   */
  updateProductForm() {
    if (!this.productForm || !this.selectedPlan) return;

    // Remove all existing selling plan inputs
    const existingSellingPlans = this.productForm.querySelectorAll('input[name="selling_plan"]');
    existingSellingPlans.forEach(input => input.remove());

    // Remove existing subscription properties
    const existingProps = this.productForm.querySelectorAll('input[name*="properties[_subscription"]');
    existingProps.forEach(input => input.remove());

    // Add selling plan if this is a subscription
    if (this.selectedPlan.sellingPlanId) {
      this.addHiddenInput('selling_plan', this.selectedPlan.sellingPlanId);
    }

    // Add custom properties for tracking
    this.addHiddenInput('properties[_subscription_plan]', this.selectedPlan.type);
    this.addHiddenInput('properties[_subscription_price]', this.selectedPlan.price);
    this.addHiddenInput('properties[_subscription_discount]', this.selectedPlan.discount);
  }

  /**
   * Add hidden input to form
   */
  addHiddenInput(name, value) {
    if (!this.productForm) return;
    
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    this.productForm.appendChild(input);
  }

  /**
   * Fire custom event for other integrations
   */
  fireSelectionEvent() {
    const event = new CustomEvent('rechargeCustomSelectionChanged', {
      detail: {
        plan: this.selectedPlan.type,
        price: this.selectedPlan.price,
        discount: this.selectedPlan.discount,
        sellingPlanId: this.selectedPlan.sellingPlanId,
        productId: this.productId
      },
      bubbles: true
    });
    
    document.dispatchEvent(event);
    
    // Also fire on the widget element
    this.widget.dispatchEvent(event);
  }

  /**
   * Get current selection
   */
  getCurrentSelection() {
    return this.selectedPlan;
  }

  /**
   * Programmatically select a plan
   */
  selectPlan(planType) {
    const radio = this.widget.querySelector(`input[value="${planType}"]`);
    if (radio) {
      radio.checked = true;
      this.handlePlanSelection(radio);
    }
  }
}

/**
 * Single boot path: avoids double init when DOM is already ready and DOMContentLoaded also fires.
 */
function bootRechargeCustomWidget() {
  if (window.__rechargeCustomWidgetBooted) return;
  window.__rechargeCustomWidgetBooted = true;
  window.rechargeCustomWidget = new RechargeCustomWidget();
  window.rechargeCustomWidget.init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootRechargeCustomWidget, { once: true });
} else {
  bootRechargeCustomWidget();
}
